import Text from '../canvas/text'
import { IndicatorType, OFFSET, PADDING, TextBaseline } from '../constants/common'
import { getTranslate } from '../controllers/renderUtils'
import Pane from '../models/pane'
import { getFirstBarToRenderIndex } from '../utils/draw_in_visible_area'
import Indicator from './indicator'

interface Attrs {
  period: string
}

const COLORS = {
  aroonUp: '#00a000',
  aroonDown: '#c80000',
}

class Aroon extends Indicator<Attrs> {
  static type = IndicatorType.Aro
  static label = 'Aroon'

  aroonUp: number[] = []
  aroonDown: number[] = []
  declare period: number

  constructor(attrs: Attrs, model: Pane) {
    super(attrs, model)
    this.renderYAxis = this.renderYAxis.bind(this)
  }

  set(values: Partial<Attrs>) {
    super.set(values)
    this.parsePeriodInt(values)
  }

  compute() {
    if (!this.isComputeNecessary()) return

    this.aroonUp = []
    this.aroonDown = []

    const startAt = this.period - 1
    for (let i = startAt; i < this.data.close.length; i++) {
      const highPeriod = this.data.high.slice(i - startAt, i + 1)
      const highestInPeriod = Math.max(...highPeriod)
      const periodSinceHigh = this.period - (highPeriod.indexOf(highestInPeriod) + 1)
      this.aroonUp[i] = 100 * ((this.period - periodSinceHigh) / this.period)

      const lowPeriod = this.data.low.slice(i - startAt, i + 1)
      const lowestInPeriod = Math.min(...lowPeriod)
      const periodSinceLow = this.period - (lowPeriod.indexOf(lowestInPeriod) + 1)
      this.aroonDown[i] = 100 * ((this.period - periodSinceLow) / this.period)
    }

    const { min, max } = this.getDomainDefaults(this.type)
    this.min = min
    this.max = max
  }

  renderIndicator(context: CanvasRenderingContext2D) {
    const firstBarToRender = getFirstBarToRenderIndex(this.data.close, this.period, this.fx, this.leftOffset)
    if (firstBarToRender === undefined) return
    let lastBarToRender = 0

    context.translate(0.5, 0.5)
    context.set('strokeStyle', COLORS.aroonDown)
    context.beginPath()
    for (let i = firstBarToRender; i < this.data.close.length; i++) {
      const x = this.fx(i)
      if (x + this.leftOffset > this.width + 10) {
        break
      }
      context.lineTo(x, Math.round(this.fy(this.aroonDown[i])))
      lastBarToRender = i
    }
    context.stroke()

    context.set('strokeStyle', COLORS.aroonUp)
    context.beginPath()
    for (let i = firstBarToRender; i <= lastBarToRender; i++) {
      context.lineTo(this.fx(i), Math.round(this.fy(this.aroonUp[i])))
    }
    context.stroke()
    context.translate(-0.5, -0.5)
  }

  renderYAxis(context: CanvasRenderingContext2D) {
    super.renderYAxis(context)
    const { IndicatorSettings, ChartSettings } = this.getChartLayoutSettings()
    const { Colors } = ChartSettings.general
    const translate = getTranslate({
      context,
      xOffset: IndicatorSettings.left.width,
      yOffset: IndicatorSettings.top.height,
    })
    translate.do()
    const text = new Text(
      {
        x: this.contentWidth + OFFSET.M - PADDING.XXS + IndicatorSettings.right.axis.margin.left!,
        font: Text.getMergedPropsWithDefaults('font', IndicatorSettings.right.axis.font),
        lineHeight: IndicatorSettings.right.axis.font.lineHeight,
        padding: {
          top: IndicatorSettings.right.axis.font?.padding?.top ?? PADDING.XXS,
          right: IndicatorSettings.right.axis.font?.padding?.right ?? PADDING.XXS,
          bottom: IndicatorSettings.right.axis.font?.padding?.bottom ?? PADDING.XXS,
          left: IndicatorSettings.right.axis.font?.padding?.left ?? PADDING.XXS,
        },
        textBaseline: TextBaseline.middle,
        fillStyle: Colors.indicatorCurrentText,
      },
      this.model
    )
    const textLabels = []
    if (this.aroonDown.length > 0) {
      textLabels.push({
        text: this.aroonDown.last()!.toFixed(2),
        y: Math.round(this.fy(this.aroonDown.last()!)),
        background: COLORS.aroonDown,
      })
    }
    if (this.aroonUp.length > 0) {
      textLabels.push({
        text: this.aroonUp.last()!.toFixed(2),
        y: Math.round(this.fy(this.aroonUp.last()!)),
        background: COLORS.aroonUp,
      })
    }

    const minY = 0
    const maxY = this.height - IndicatorSettings.bottom.height - IndicatorSettings.top.height
    textLabels.forEach((labelSetting) => {
      if (labelSetting.y >= minY && labelSetting.y <= maxY) {
        text.set(labelSetting).render(context)
      }
    })

    translate.undo()
  }

  getModalConfig() {
    const period = {
      type: 'number',
      label: 'Period',
      name: 'period',
      value: this.period ?? 25,
      required: true,
      min: 1,
      max: 999999,
    }

    return {
      title: this.label,
      inputs: [period],
      inputsErrorMessages: {
        period: `${period.label} must be a whole number between ${period.min} and ${period.max}`,
      },
    }
  }

  getIsValid(key: string): boolean {
    switch (key) {
      case 'period':
        return this.getIsNumberInputValid({ key })
      default:
        return false
    }
  }

  toString() {
    return `Aroon ${this.period}`
  }
}

export default Aroon
